// Day 3 - Full Production Content
// Temat: Dlaczego AI czasem nie rozumie Cię tak, jak chcesz – i jak to naprawić
// Ton: przyjazny, lekki, bez żargonu, premium
// Duration: 12-15 minutes

export const day3Content = {
  day_number: 3,
  title: "Jak mówić do AI, żeby Cię rozumiało",
  subtitle: "Struktura promptu + Praktyka",
  duration: "12-15 min",
  exercises: [
    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 1: Dlaczego AI czasem nie rozumie (6 kroków)
    // Cel: Uświadomić, że AI reaguje dosłownie na to, co napiszesz
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d3_ex1",
      title: "Jak AI naprawdę działa",
      icon: "brain",
      steps: [
        {
          id: "d3_ex1_s1",
          type: "choice",
          title: "Ważna prawda",
          content: "AI czyta Twój prompt i odpowiada na podstawie tego, co napisałeś. Jeśli napiszesz coś ogólnego, co dostaniesz?",
          options: [
            { id: "a", text: "Odpowiedź ogólną, bez konkretów", correct: true },
            { id: "b", text: "AI zgadnie, czego naprawdę potrzebujesz", correct: false },
            { id: "c", text: "AI poprosi o więcej informacji", correct: false },
            { id: "d", text: "Odpowiedź idealnie dopasowaną do Ciebie", correct: false }
          ],
          shuffle: true,
          softHint: "AI nie czyta w myślach. Wie tylko to, co mu powiesz.",
          successMessage: "Właśnie tak. AI nie zgaduje – reaguje na to, co dostaje."
        },
        {
          id: "d3_ex1_s2",
          type: "choice",
          title: "Prosty przykład",
          content: "Piszesz: \"Napisz mi post\". Co jest nie tak z tym poleceniem?",
          options: [
            { id: "a", text: "Jest za krótkie", correct: false },
            { id: "b", text: "Brakuje informacji: o czym, dla kogo, w jakim stylu", correct: true },
            { id: "c", text: "Powinno być po angielsku", correct: false },
            { id: "d", text: "Nic, to dobre polecenie", correct: false }
          ],
          shuffle: true,
          softHint: "Pomyśl: czy Ty wiedziałbyś, co napisać, gdyby ktoś dał Ci tylko tyle informacji?",
          successMessage: "Dokładnie. Bez kontekstu AI musi zgadywać – i zwykle trafia ogólnikowo."
        },
        {
          id: "d3_ex1_s3",
          type: "choice",
          title: "Efekt ogólnika",
          content: "Gdy prompt jest zbyt ogólny, AI zwykle odpowiada:",
          options: [
            { id: "a", text: "Bardzo szczegółowo i konkretnie", correct: false },
            { id: "b", text: "Bezpiecznie i powierzchownie, żeby niczego nie pomylić", correct: true },
            { id: "c", text: "Pytaniami o więcej szczegółów", correct: false },
            { id: "d", text: "Błędnymi informacjami", correct: false }
          ],
          shuffle: true,
          softHint: "AI woli dać Ci coś \"bezpiecznego\" niż ryzykować, że źle zrozumie.",
          successMessage: "Tak jest. Dlatego ogólne polecenia dają nudne, przewidywalne odpowiedzi."
        },
        {
          id: "d3_ex1_s4",
          type: "reorder",
          title: "Przyczyna i skutek",
          content: "Ułóż w kolejności, co się dzieje, gdy piszesz ogólny prompt:",
          items: [
            { id: "r1", text: "Piszesz ogólne polecenie bez szczegółów", order: 1 },
            { id: "r2", text: "AI nie wie, czego dokładnie potrzebujesz", order: 2 },
            { id: "r3", text: "AI wybiera bezpieczną, ogólną odpowiedź", order: 3 },
            { id: "r4", text: "Dostajesz tekst, który Ci nie pasuje", order: 4 }
          ],
          softHint: "Wszystko zaczyna się od tego, co napiszesz.",
          successMessage: "Widzisz ten łańcuch? Kontrolujesz go od pierwszego kroku."
        },
        {
          id: "d3_ex1_s5",
          type: "choice",
          title: "Kluczowy wniosek",
          content: "Jeśli chcesz lepszą odpowiedź od AI, musisz:",
          options: [
            { id: "a", text: "Użyć droższego modelu AI", correct: false },
            { id: "b", text: "Napisać więcej słów", correct: false },
            { id: "c", text: "Dać AI więcej konkretnych informacji", correct: true },
            { id: "d", text: "Pisać po angielsku", correct: false }
          ],
          shuffle: true,
          softHint: "Nie chodzi o ilość słów ani o technologię. Chodzi o jakość informacji.",
          successMessage: "To jest klucz. Konkret w prompcie = konkret w odpowiedzi."
        },
        {
          id: "d3_ex1_s6",
          type: "fill",
          title: "Zapamiętaj zasadę",
          content: "Dokończ zdanie:",
          template: "Im więcej _______ dajesz AI, tym lepsza odpowiedź.",
          correctAnswers: ["konkretów", "szczegółów", "informacji", "kontekstu", "detali"],
          minLength: 5,
          softHint: "Pomyśl: co sprawia, że AI lepiej rozumie, czego potrzebujesz?",
          successMessage: "Dokładnie tak. To jest fundament skutecznej komunikacji z AI."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 2: Prosta struktura promptu (7 kroków)
    // Cel: Nauczyć 4-elementowej struktury bez technicznego żargonu
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d3_ex2",
      title: "4 pytania, które zmienią Twoje prompty",
      icon: "layout",
      steps: [
        {
          id: "d3_ex2_s1",
          type: "choice",
          title: "Prosta metoda",
          content: "Zanim napiszesz prompt, zadaj sobie 4 pytania. Które z nich jest PIERWSZE?",
          options: [
            { id: "a", text: "Jak długa ma być odpowiedź?", correct: false },
            { id: "b", text: "Kim ma być AI w tej rozmowie?", correct: true },
            { id: "c", text: "Czy użyć emoji?", correct: false },
            { id: "d", text: "Jaki model wybrać?", correct: false }
          ],
          shuffle: true,
          softHint: "Pomyśl: czy rozmawiasz z ekspertem, przyjacielem, czy nauczycielem?",
          successMessage: "Tak. Rola AI to pierwszy krok – określa ton i perspektywę odpowiedzi."
        },
        {
          id: "d3_ex2_s2",
          type: "choice",
          title: "Pytanie drugie",
          content: "Po określeniu roli, co powinno być następne?",
          options: [
            { id: "a", text: "Co dokładnie AI ma zrobić?", correct: true },
            { id: "b", text: "W jakim języku pisać?", correct: false },
            { id: "c", text: "Ile słów użyć?", correct: false },
            { id: "d", text: "Czy dodać pozdrowienia?", correct: false }
          ],
          shuffle: true,
          softHint: "AI musi wiedzieć, jakie jest Twoje konkretne zadanie.",
          successMessage: "Właśnie. Jasny cel to podstawa – AI musi wiedzieć, co ma dostarczyć."
        },
        {
          id: "d3_ex2_s3",
          type: "choice",
          title: "Pytanie trzecie",
          content: "Masz rolę i cel. Co jeszcze pomoże AI lepiej trafić?",
          options: [
            { id: "a", text: "Pora dnia", correct: false },
            { id: "b", text: "Dla kogo to jest? Kto to przeczyta?", correct: true },
            { id: "c", text: "Twoje imię", correct: false },
            { id: "d", text: "Data wysłania", correct: false }
          ],
          shuffle: true,
          softHint: "Tekst dla nastolatka wygląda inaczej niż dla dyrektora firmy.",
          successMessage: "Świetnie. Kontekst odbiorcy zmienia wszystko – styl, słownictwo, głębię."
        },
        {
          id: "d3_ex2_s4",
          type: "choice",
          title: "Pytanie czwarte",
          content: "Ostatnie pytanie dotyczy tego, JAK ma wyglądać odpowiedź. Co to oznacza?",
          options: [
            { id: "a", text: "Czy AI ma być grzeczne", correct: false },
            { id: "b", text: "Format: lista, akapit, punkty, tabela?", correct: true },
            { id: "c", text: "Czy odpowiedź ma być długa", correct: false },
            { id: "d", text: "Kolor tekstu", correct: false }
          ],
          shuffle: true,
          softHint: "Czasem potrzebujesz listy, czasem pełnego tekstu, czasem tabeli.",
          successMessage: "Dokładnie. Format kontroluje kształt odpowiedzi – to Ty decydujesz."
        },
        {
          id: "d3_ex2_s5",
          type: "reorder",
          title: "Ułóż strukturę",
          content: "Ułóż 4 elementy dobrego promptu w logicznej kolejności:",
          items: [
            { id: "r1", text: "ROLA: Kim jest AI?", order: 1 },
            { id: "r2", text: "CEL: Co ma zrobić?", order: 2 },
            { id: "r3", text: "ODBIORCA: Dla kogo to jest?", order: 3 },
            { id: "r4", text: "FORMAT: Jak ma wyglądać?", order: 4 }
          ],
          softHint: "Zacznij od roli, zakończ na formacie.",
          successMessage: "To jest Twoja nowa struktura. Prosta, ale potężna."
        },
        {
          id: "d3_ex2_s6",
          type: "choice",
          title: "Test zrozumienia",
          content: "\"Napisz jako trener fitness plan treningu dla początkującego w formie listy\" – które elementy struktury zawiera ten prompt?",
          options: [
            { id: "a", text: "Tylko cel", correct: false },
            { id: "b", text: "Rolę, cel, odbiorcę i format", correct: true },
            { id: "c", text: "Tylko rolę i cel", correct: false },
            { id: "d", text: "Tylko format", correct: false }
          ],
          shuffle: true,
          softHint: "Przeczytaj uważnie: trener = rola, plan = cel, początkujący = odbiorca, lista = format.",
          successMessage: "Brawo. Widzisz, jak te 4 elementy współpracują?"
        },
        {
          id: "d3_ex2_s7",
          type: "fill",
          title: "Zapamiętaj formułę",
          content: "Uzupełnij: ROLA + CEL + _______ + FORMAT = skuteczny prompt",
          template: "ROLA + CEL + _______ + FORMAT = skuteczny prompt",
          correctAnswers: ["odbiorca", "kontekst", "dla kogo", "odbiorcy", "kto"],
          minLength: 3,
          softHint: "Kto przeczyta to, co AI napisze?",
          successMessage: "Masz to. Ta formuła będzie Ci służyć w każdej sytuacji."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 3: Przed i Po – praktyczny przykład (5 kroków)
    // Cel: Pokazać różnicę między słabym a dobrym promptem
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d3_ex3",
      title: "Porównanie: słaby vs. skuteczny prompt",
      icon: "scale",
      steps: [
        {
          id: "d3_ex3_s1",
          type: "choice",
          title: "Słaby prompt",
          content: "Oto prompt: \"Napisz mi post o zdrowym jedzeniu\"\n\nCo jest z nim nie tak?",
          options: [
            { id: "a", text: "Jest za krótki", correct: false },
            { id: "b", text: "Brakuje roli, odbiorcy i formatu", correct: true },
            { id: "c", text: "Temat jest zły", correct: false },
            { id: "d", text: "Powinien być po angielsku", correct: false }
          ],
          shuffle: true,
          softHint: "Przeanalizuj: czy ten prompt mówi KIM ma być AI? DLA KOGO pisze? JAK ma wyglądać?",
          successMessage: "Właśnie. Ten prompt nie daje AI żadnego kierunku – tylko ogólny temat."
        },
        {
          id: "d3_ex3_s2",
          type: "choice",
          title: "Analiza słabego promptu",
          content: "Gdy AI dostanie prompt \"Napisz mi post o zdrowym jedzeniu\", co prawdopodobnie zrobi?",
          options: [
            { id: "a", text: "Napisze idealny post dopasowany do Ciebie", correct: false },
            { id: "b", text: "Napisze ogólny, nudny tekst o witaminach i warzywach", correct: true },
            { id: "c", text: "Poprosi o więcej informacji", correct: false },
            { id: "d", text: "Odmówi napisania", correct: false }
          ],
          shuffle: true,
          softHint: "AI nie wie, czy piszesz dla sportowców, mam, czy seniorów.",
          successMessage: "Tak. Bez kontekstu AI wybiera najbezpieczniejszą, najbardziej ogólną odpowiedź."
        },
        {
          id: "d3_ex3_s3",
          type: "choice",
          title: "Lepszy prompt",
          content: "Oto ulepszony prompt:\n\n\"Jesteś dietetykiem. Napisz krótki post na Instagram o tym, jak proste nawyki żywieniowe mogą poprawić energię w ciągu dnia. Pisz dla zapracowanych osób 25-35 lat. Użyj 3 konkretnych rad w formie listy.\"\n\nIle elementów struktury zawiera?",
          options: [
            { id: "a", text: "1 element", correct: false },
            { id: "b", text: "2 elementy", correct: false },
            { id: "c", text: "3 elementy", correct: false },
            { id: "d", text: "4 elementy: rola, cel, odbiorca, format", correct: true }
          ],
          shuffle: true,
          softHint: "Policz: dietetyk = ?, post o nawykach = ?, zapracowani 25-35 = ?, lista 3 rad = ?",
          successMessage: "Wszystkie 4 elementy są na miejscu. To kompletny, skuteczny prompt."
        },
        {
          id: "d3_ex3_s4",
          type: "choice",
          title: "Dlaczego to działa lepiej?",
          content: "Co sprawia, że ulepszony prompt da lepszą odpowiedź?",
          options: [
            { id: "a", text: "Jest dłuższy", correct: false },
            { id: "b", text: "AI wie dokładnie: jako kto mówi, co ma napisać, dla kogo i w jakiej formie", correct: true },
            { id: "c", text: "Używa trudniejszych słów", correct: false },
            { id: "d", text: "Zawiera więcej pytań", correct: false }
          ],
          shuffle: true,
          softHint: "Nie chodzi o długość – chodzi o konkretne informacje.",
          successMessage: "Dokładnie. AI ma wszystko, czego potrzebuje, żeby trafić w punkt."
        },
        {
          id: "d3_ex3_s5",
          type: "choice",
          title: "Efekt końcowy",
          content: "Dzięki dobrej strukturze promptu, odpowiedź AI będzie:",
          options: [
            { id: "a", text: "Bardziej trafiona, konkretna i użyteczna", correct: true },
            { id: "b", text: "Dłuższa, ale niekoniecznie lepsza", correct: false },
            { id: "c", text: "Napisana szybciej", correct: false },
            { id: "d", text: "Zawsze perfekcyjna", correct: false }
          ],
          shuffle: true,
          softHint: "Dobra struktura nie gwarantuje perfekcji, ale znacząco zwiększa szanse na trafienie.",
          successMessage: "Właśnie tak. To nie magia – to precyzja komunikacji."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 4: Twoje pierwsze ćwiczenie (5 kroków)
    // Cel: Użytkownik tworzy własny prompt według struktury
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d3_ex4",
      title: "Stwórz własny prompt",
      icon: "pencil",
      steps: [
        {
          id: "d3_ex4_s1",
          type: "choice",
          title: "Wybierz temat",
          content: "Zaraz stworzysz własny prompt. Który z tych tematów Cię interesuje?",
          options: [
            { id: "a", text: "Post do social media", isChoice: true },
            { id: "b", text: "Email do klienta/współpracownika", isChoice: true },
            { id: "c", text: "Opis produktu lub usługi", isChoice: true },
            { id: "d", text: "Plan lub lista zadań", isChoice: true }
          ],
          shuffle: false,
          softHint: "Nie ma złej odpowiedzi – wybierz to, co może Ci się przydać.",
          successMessage: "Dobry wybór. Teraz zbudujemy prompt krok po kroku."
        },
        {
          id: "d3_ex4_s2",
          type: "shortPrompt",
          title: "Element 1: Rola",
          content: "Kim ma być AI? Napisz krótko rolę, np. \"copywriter\", \"ekspert HR\", \"trener biznesu\".",
          placeholder: "Wpisz rolę dla AI...",
          minLength: 3,
          requiredElements: [],
          softHint: "Pomyśl: kto normalnie pisałby taki tekst w prawdziwym życiu?",
          successMessage: "Świetnie. AI wie już, jaką perspektywę przyjąć."
        },
        {
          id: "d3_ex4_s3",
          type: "shortPrompt",
          title: "Element 2: Cel",
          content: "Co AI ma zrobić? Opisz konkretne zadanie, np. \"napisz post zachęcający do zapisania się na newsletter\".",
          placeholder: "Wpisz zadanie dla AI...",
          minLength: 10,
          requiredElements: [],
          softHint: "Bądź konkretny – co dokładnie ma powstać?",
          successMessage: "Teraz AI wie, co ma dostarczyć. Idziemy dalej."
        },
        {
          id: "d3_ex4_s4",
          type: "shortPrompt",
          title: "Element 3: Odbiorca",
          content: "Dla kogo to jest? Opisz odbiorcę, np. \"właściciele małych firm\", \"młode mamy\", \"studenci\".",
          placeholder: "Wpisz, dla kogo jest ten tekst...",
          minLength: 5,
          requiredElements: [],
          softHint: "Im dokładniej opiszesz odbiorcę, tym lepiej AI dopasuje ton i styl.",
          successMessage: "Doskonale. AI dostosuje język do Twojej grupy docelowej."
        },
        {
          id: "d3_ex4_s5",
          type: "shortPrompt",
          title: "Element 4: Format",
          content: "Jak ma wyglądać odpowiedź? Np. \"3 punkty\", \"krótki akapit\", \"lista z nagłówkami\".",
          placeholder: "Wpisz oczekiwany format...",
          minLength: 3,
          requiredElements: [],
          softHint: "Format to kształt odpowiedzi – lista, tekst ciągły, tabela?",
          successMessage: "Właśnie stworzyłeś kompletny, przemyślany prompt. To jest progres."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 5: Mini quiz – sprawdzenie zrozumienia (4 kroki)
    // Cel: Utrwalić wiedzę bez stresu
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d3_ex5",
      title: "Sprawdź się",
      icon: "check",
      steps: [
        {
          id: "d3_ex5_s1",
          type: "choice",
          title: "Pytanie 1 z 3",
          content: "Dlaczego ogólny prompt daje ogólną odpowiedź?",
          options: [
            { id: "a", text: "Bo AI jest leniwe", correct: false },
            { id: "b", text: "Bo AI nie ma wystarczających informacji, żeby trafić w punkt", correct: true },
            { id: "c", text: "Bo AI nie rozumie polskiego", correct: false },
            { id: "d", text: "Bo używasz złego modelu", correct: false }
          ],
          shuffle: true,
          softHint: "Wróć do zasady: AI reaguje na to, co mu dasz.",
          successMessage: "Dokładnie. To nie wina AI – to kwestia tego, co mu przekazujesz."
        },
        {
          id: "d3_ex5_s2",
          type: "choice",
          title: "Pytanie 2 z 3",
          content: "Która kolejność elementów promptu jest prawidłowa?",
          options: [
            { id: "a", text: "Format, Cel, Rola, Odbiorca", correct: false },
            { id: "b", text: "Cel, Format, Odbiorca, Rola", correct: false },
            { id: "c", text: "Rola, Cel, Odbiorca, Format", correct: true },
            { id: "d", text: "Odbiorca, Rola, Format, Cel", correct: false }
          ],
          shuffle: true,
          softHint: "Zacznij od tego, KIM jest AI, a zakończ na tym, JAK ma wyglądać odpowiedź.",
          successMessage: "Masz to. Ta kolejność pomaga logicznie budować każdy prompt."
        },
        {
          id: "d3_ex5_s3",
          type: "choice",
          title: "Pytanie 3 z 3",
          content: "Co daje Ci określenie ODBIORCY w prompcie?",
          options: [
            { id: "a", text: "AI pisze szybciej", correct: false },
            { id: "b", text: "AI dostosowuje ton, styl i głębokość do konkretnej grupy", correct: true },
            { id: "c", text: "Odpowiedź jest dłuższa", correct: false },
            { id: "d", text: "Nic szczególnego", correct: false }
          ],
          shuffle: true,
          softHint: "Tekst dla eksperta wygląda inaczej niż dla początkującego.",
          successMessage: "Świetnie. Odbiorca to jeden z najważniejszych elementów precyzji."
        },
        {
          id: "d3_ex5_s4",
          type: "choice",
          title: "Bonus: zastosowanie",
          content: "Chcesz napisać email z przeprosinami do klienta. Który prompt jest lepszy?",
          options: [
            { id: "a", text: "\"Napisz email z przeprosinami\"", correct: false },
            { id: "b", text: "\"Jako specjalista ds. obsługi klienta napisz profesjonalny, empatyczny email z przeprosinami za opóźnienie dostawy. Odbiorca: klient premium. Format: krótki, 3 akapity.\"", correct: true },
            { id: "c", text: "\"Email. Przeprosiny. Klient.\"", correct: false },
            { id: "d", text: "\"Napisz coś miłego do klienta\"", correct: false }
          ],
          shuffle: true,
          softHint: "Który prompt daje AI więcej informacji do pracy?",
          successMessage: "Widzisz różnicę? Teraz świadomie budujesz lepsze prompty."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 6: Podsumowanie dnia (3 kroki)
    // Cel: Zamknąć dzień z poczuciem postępu i ciekawością na jutro
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d3_ex6",
      title: "Co dziś zrozumiałeś",
      icon: "award",
      steps: [
        {
          id: "d3_ex6_s1",
          type: "choice",
          title: "Kluczowa lekcja",
          content: "Dziś nauczyłeś się, że AI:",
          options: [
            { id: "a", text: "Czyta w myślach i zgaduje, czego potrzebujesz", correct: false },
            { id: "b", text: "Reaguje dokładnie na to, co napiszesz – bez domysłów", correct: true },
            { id: "c", text: "Zawsze daje idealne odpowiedzi", correct: false },
            { id: "d", text: "Działa losowo", correct: false }
          ],
          shuffle: true,
          softHint: "To jest fundament wszystkiego, czego się uczysz.",
          successMessage: "To jest właśnie ta świadomość, która daje Ci przewagę."
        },
        {
          id: "d3_ex6_s2",
          type: "choice",
          title: "Twoje narzędzie",
          content: "Od dziś masz prostą strukturę do budowania promptów. Jakie są jej 4 elementy?",
          options: [
            { id: "a", text: "Rola, Cel, Odbiorca, Format", correct: true },
            { id: "b", text: "Temat, Długość, Język, Emoji", correct: false },
            { id: "c", text: "Pytanie, Odpowiedź, Komentarz, Koniec", correct: false },
            { id: "d", text: "Start, Środek, Koniec, Podpis", correct: false }
          ],
          shuffle: true,
          softHint: "Przypomnij sobie 4 pytania, które zadajesz przed napisaniem promptu.",
          successMessage: "Ta struktura będzie Ci służyć w każdej rozmowie z AI."
        },
        {
          id: "d3_ex6_s3",
          type: "choice",
          title: "Przed Tobą",
          content: "Dzień 3 za Tobą. Co czeka w Dniu 4?",
          options: [
            { id: "a", text: "Nauczysz się, jak AI może pomóc Ci w codziennej pracy", correct: true },
            { id: "b", text: "Będziesz uczyć się programowania", correct: false },
            { id: "c", text: "Test z całego kursu", correct: false },
            { id: "d", text: "Koniec nauki", correct: false }
          ],
          shuffle: false,
          softHint: "Kolejny dzień to praktyczne zastosowania w Twojej pracy.",
          successMessage: "Widzimy się jutro. Czeka Cię coś, co możesz od razu wykorzystać."
        }
      ]
    }
  ]
};
